import { useCallback, useEffect, useRef, type ReactNode } from 'react'
import { classifyResultText, parseScoreText, trackGameEvent } from '../lib/analytics'

const interactionKeys = new Set([
  'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', ' ',
  'w', 'a', 's', 'd', 'W', 'A', 'S', 'D',
  '1', '2', '3', '4', '5', '6', '7', '8', '9',
])

const restartPattern = /restart|try again|new (?:game|puzzle|map|garden|route|code|circuit|lab|delivery|harbor|calibration)|again|重开|重新|再来|下一局|新(?:游戏|地图|花园|路线|密码|电路|实验|配送|清单|校准)/i

type Session = { started: boolean; ended: boolean; seconds: number; sent30: boolean; sent60: boolean }
const emptySession = (): Session => ({ started: false, ended: false, seconds: 0, sent30: false, sent60: false })

export default function GameTelemetry({ gameId, challengeKey, children }: { gameId: string; challengeKey?: string; children: ReactNode }) {
  const rootRef = useRef<HTMLDivElement>(null)
  const sessionRef = useRef<Session>(emptySession())
  const pointerWasStartedRef = useRef(false)

  const eventParams = useCallback(() => ({ level_name: gameId, challenge_date: challengeKey }), [gameId, challengeKey])

  const startRound = useCallback(() => {
    if (sessionRef.current.started && !sessionRef.current.ended) return
    sessionRef.current = { ...emptySession(), started: true }
    trackGameEvent('level_start', gameId, eventParams())
  }, [eventParams, gameId])

  const restartRound = useCallback(() => {
    trackGameEvent('restart', gameId, eventParams())
    sessionRef.current = emptySession()
    startRound()
  }, [eventParams, gameId, startRound])

  useEffect(() => {
    sessionRef.current = emptySession()
  }, [gameId, challengeKey])

  useEffect(() => {
    const timer = window.setInterval(() => {
      const session = sessionRef.current
      if (!session.started || session.ended || document.visibilityState !== 'visible') return
      session.seconds += 1
      if (session.seconds >= 30 && !session.sent30) {
        session.sent30 = true
        trackGameEvent('play_30_seconds', gameId, { ...eventParams(), engagement_seconds: 30 })
      }
      if (session.seconds >= 60 && !session.sent60) {
        session.sent60 = true
        trackGameEvent('play_60_seconds', gameId, { ...eventParams(), engagement_seconds: 60 })
      }
    }, 1000)
    return () => window.clearInterval(timer)
  }, [eventParams, gameId])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'r' && sessionRef.current.started) {
        restartRound()
        return
      }
      if (interactionKeys.has(event.key)) startRound()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [restartRound, startRound])

  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const inspect = () => {
      if (!sessionRef.current.started || sessionRef.current.ended) return
      const overlay = root.querySelector<HTMLElement>('.overlay')
      if (!overlay) return
      const result = classifyResultText(overlay.innerText)
      if (!result) return
      sessionRef.current.ended = true
      const score = parseScoreText(overlay.querySelector<HTMLElement>('.overlay-badge')?.innerText ?? '')
      const params = { ...eventParams(), success: result === 'won', score, engagement_seconds: sessionRef.current.seconds }
      trackGameEvent('level_end', gameId, params)
      if (score !== undefined) trackGameEvent('post_score', gameId, params)
    }
    const observer = new MutationObserver(inspect)
    observer.observe(root, { childList: true, subtree: true, characterData: true })
    inspect()
    return () => observer.disconnect()
  }, [eventParams, gameId])

  return <div
    ref={rootRef}
    className="game-telemetry"
    onPointerDownCapture={event => {
      const target = event.target as Element
      if (!target.closest('button, canvas, [role="gridcell"], [role="button"]')) return
      pointerWasStartedRef.current = sessionRef.current.started
      startRound()
    }}
    onClickCapture={event => {
      const button = (event.target as Element).closest('button')
      if (!button || !pointerWasStartedRef.current || !restartPattern.test(button.textContent ?? '')) return
      restartRound()
    }}
  >{children}</div>
}
